import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesRight,
  faCheck,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import { theme } from "../../assets/theme/theme";

const SwipeButton = ({
  onSwipeSuccess,
  text = "Swipe to Accept",
  activeText = "Accepting Trip...",
  disabled = false,
}) => {
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const maxDragRef = useRef(0);

  const updateMaxDrag = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const thumbWidth = 48;
      const padding = 8;
      maxDragRef.current = Math.max(0, containerWidth - thumbWidth - padding);
    }
  }, []);

  useEffect(() => {
    updateMaxDrag();
    window.addEventListener("resize", updateMaxDrag);
    return () => window.removeEventListener("resize", updateMaxDrag);
  }, [updateMaxDrag]);

  const handleStart = (clientX) => {
    if (disabled || isCompleted || isLoading) return;
    setIsDragging(true);
    startXRef.current = clientX - dragX;
  };

  const handleMove = useCallback(
    (clientX) => {
      if (!isDragging || isCompleted || isLoading) return;
      const currentX = clientX - startXRef.current;
      const boundedX = Math.min(Math.max(0, currentX), maxDragRef.current);
      setDragX(boundedX);
    },
    [isDragging, isCompleted, isLoading]
  );

  const handleEnd = useCallback(async () => {
    if (!isDragging || isCompleted || isLoading) return;
    setIsDragging(false);

    // Trigger threshold set to 90%
    if (dragX >= maxDragRef.current * 0.9) {
      setDragX(maxDragRef.current);
      setIsLoading(true);

      try {
        if (onSwipeSuccess) {
          // Await parent async network request
          await onSwipeSuccess();
        }
        setIsCompleted(true);
      } catch (err) {
        // Network/409 failure: Snap back to start and re-enable swipe
        setIsCompleted(false);
        setDragX(0);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Snap back if released before threshold
      setDragX(0);
    }
  }, [isDragging, isCompleted, isLoading, dragX, onSwipeSuccess]);

  useEffect(() => {
    const onMouseMove = (e) => handleMove(e.clientX);
    const onMouseUp = () => handleEnd();
    const onTouchMove = (e) => handleMove(e.touches[0].clientX);
    const onTouchEnd = () => handleEnd();

    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
      window.addEventListener("touchmove", onTouchMove);
      window.addEventListener("touchend", onTouchEnd);
    }

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [isDragging, handleMove, handleEnd]);

  const progressRatio = maxDragRef.current > 0 ? dragX / maxDragRef.current : 0;

  const defaultTextOpacity = Math.max(0, 1 - progressRatio * 2.2);
  const activeTextOpacity = Math.min(1, Math.max(0, (progressRatio - 0.25) * 1.8));

  return (
    <div
      ref={containerRef}
      className={`relative select-none overflow-hidden flex items-center p-1 border transition-all ${
        disabled || isLoading ? "pointer-events-none" : ""
      } ${disabled ? "opacity-50" : ""}`}
      style={{
        height: "56px",
        backgroundColor: theme.colors.grey001,
        borderColor: theme.colors.grey005,
        borderRadius: `${theme.radius[16]}px`,
      }}
    >
      {/* Dynamic Background Progress Fill */}
      <div
        className="absolute left-0 top-0 bottom-0 pointer-events-none"
        style={{
          width: `${dragX + 48}px`,
          backgroundColor: isCompleted
            ? theme.colors.success
            : theme.colors.brand,
          opacity: 0.15 + progressRatio * 0.85,
          transition: isDragging
            ? "none"
            : "width 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.3s ease-out, background-color 0.2s ease",
        }}
      />

      {/* 1. Default Idle Text */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none px-12"
        style={{
          opacity: isCompleted || isLoading ? 0 : defaultTextOpacity,
          transition: isDragging ? "none" : "opacity 0.2s ease-out",
        }}
      >
        <span
          className="text-xs uppercase tracking-wider font-bold"
          style={{
            fontFamily: "DMSans-Medium",
            color: theme.colors.bitumen,
          }}
        >
          {text}
        </span>
      </div>

      {/* 2. Active Swiping / Loading / Accepted Text */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none px-12"
        style={{
          opacity: isCompleted || isLoading ? 1 : activeTextOpacity,
          transition: isDragging ? "none" : "opacity 0.2s ease-out",
        }}
      >
        <span
          className="text-xs uppercase tracking-wider font-bold"
          style={{
            fontFamily: "DMSans-SemiBold",
            color: isCompleted
              ? theme.colors.pureWhite
              : progressRatio > 0.6 || isLoading
              ? theme.colors.pureWhite
              : theme.colors.brand,
          }}
        >
          {isCompleted
            ? "Trip Accepted!"
            : isLoading
            ? "Accepting..."
            : activeText}
        </span>
      </div>

      {/* 3. Slider Thumb Handle */}
      <div
        onMouseDown={(e) => handleStart(e.clientX)}
        onTouchStart={(e) => handleStart(e.touches[0].clientX)}
        className="relative z-10 flex items-center justify-center shadow-md cursor-grab active:cursor-grabbing shrink-0"
        style={{
          width: "48px",
          height: "48px",
          borderRadius: `${theme.radius[12]}px`,
          backgroundColor: isCompleted
            ? theme.colors.success
            : theme.colors.brand,
          transform: `translateX(${dragX}px)`,
          transition: isDragging
            ? "none"
            : "transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), background-color 0.2s ease",
          boxShadow: `0 4px 12px ${theme.colors.brand}40`,
        }}
      >
        {isLoading ? (
          <FontAwesomeIcon
            icon={faCircleNotch}
            className="text-white text-sm animate-spin"
          />
        ) : (
          <FontAwesomeIcon
            icon={isCompleted ? faCheck : faAnglesRight}
            className={`text-white text-sm ${
              !isDragging && !isCompleted ? "animate-pulse" : ""
            }`}
          />
        )}
      </div>
    </div>
  );
};

export default SwipeButton;